import {
  capVolumeByPriceDateSegments,
  formatVolumeByPriceDateLabel,
  VOLUME_BY_PRICE_BUY_FILL,
  VOLUME_BY_PRICE_DATE_FILL_OPACITY,
  VOLUME_BY_PRICE_DATE_HIGHLIGHT_OPACITY,
  VOLUME_BY_PRICE_FILL,
  VOLUME_BY_PRICE_FILL_OPACITY,
  VOLUME_BY_PRICE_MAX_WIDTH_FRAC,
  VOLUME_BY_PRICE_NEUTRAL_FILL,
  VOLUME_BY_PRICE_SELL_FILL,
  VOLUME_BY_PRICE_SIDE_FILL_OPACITY,
  volumeByPriceColorForDate,
  type VolumeByPriceBar,
  type VolumeByPriceColorMode,
} from '@/utils/volumeByPriceLayer';

export type VolumeByPriceChartMetrics = {
  yAxisMap?: Record<string, { scale?: (v: number) => number }>;
  offset?: { top: number; left: number; width: number; height: number };
};

type Props = {
  bars: VolumeByPriceBar[];
  chartProps: VolumeByPriceChartMetrics;
  yAxisId?: string;
  bucketCents?: number;
  /** Skip bins below this fraction of max volume (reduces noise). */
  minVolumeFrac?: number;
  colorMode?: VolumeByPriceColorMode;
  /** Chronological session dates in the visible range (segment colors). */
  dateOrder?: string[];
  /** Emphasize this session date when colorMode is byDate. */
  highlightDate?: string | null;
};

type LinearScale = ((v: number) => number) & {
  domain?: () => [number, number];
};

type OverlayRect = {
  key: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  fillOpacity: number;
  title?: string;
};

function resolveYScale(
  yAxisMap: VolumeByPriceChartMetrics['yAxisMap'],
  yAxisId: string,
): LinearScale | null {
  if (!yAxisMap) return null;
  const candidates = [
    yAxisMap[yAxisId],
    yAxisMap[0],
    yAxisMap['0'],
    ...Object.values(yAxisMap),
  ];
  for (const axis of candidates) {
    const scale = axis?.scale;
    if (typeof scale === 'function') return scale as LinearScale;
  }
  return null;
}

function plotY(yScale: LinearScale, offsetTop: number, price: number): number | null {
  const raw = Number(yScale(price));
  if (!Number.isFinite(raw)) return null;
  return offsetTop + raw;
}

function pushTotalRect(
  rects: OverlayRect[],
  key: string,
  plotRight: number,
  top: number,
  width: number,
  height: number,
): void {
  rects.push({
    key,
    x: plotRight - width,
    y: top,
    width,
    height,
    fill: VOLUME_BY_PRICE_FILL,
    fillOpacity: VOLUME_BY_PRICE_FILL_OPACITY,
  });
}

function pushSideRects(
  rects: OverlayRect[],
  keyPrefix: string,
  plotRight: number,
  top: number,
  totalWidth: number,
  height: number,
  bar: VolumeByPriceBar,
): void {
  const buy = Math.max(0, bar.buy_volume ?? 0);
  const sell = Math.max(0, bar.sell_volume ?? 0);
  const neutral = Math.max(0, bar.neutral_volume ?? 0);
  const attributed = buy + sell + neutral;
  if (attributed <= 0) {
    pushTotalRect(rects, `${keyPrefix}-total`, plotRight, top, totalWidth, height);
    return;
  }

  const sellW = (sell / attributed) * totalWidth;
  const buyW = (buy / attributed) * totalWidth;
  const neutralW = Math.max(0, totalWidth - sellW - buyW);

  let x = plotRight - totalWidth;
  if (buyW > 0) {
    rects.push({
      key: `${keyPrefix}-buy`,
      x,
      y: top,
      width: buyW,
      height,
      fill: VOLUME_BY_PRICE_BUY_FILL,
      fillOpacity: VOLUME_BY_PRICE_SIDE_FILL_OPACITY,
    });
    x += buyW;
  }
  if (neutralW > 0.25) {
    rects.push({
      key: `${keyPrefix}-neutral`,
      x,
      y: top,
      width: neutralW,
      height,
      fill: VOLUME_BY_PRICE_NEUTRAL_FILL,
      fillOpacity: VOLUME_BY_PRICE_SIDE_FILL_OPACITY * 0.85,
    });
    x += neutralW;
  }
  if (sellW > 0) {
    rects.push({
      key: `${keyPrefix}-sell`,
      x,
      y: top,
      width: sellW,
      height,
      fill: VOLUME_BY_PRICE_SELL_FILL,
      fillOpacity: VOLUME_BY_PRICE_SIDE_FILL_OPACITY,
    });
  }
}

function pushDateRects(
  rects: OverlayRect[],
  keyPrefix: string,
  plotRight: number,
  top: number,
  totalWidth: number,
  height: number,
  bar: VolumeByPriceBar,
  dateOrder: string[],
  highlightDate: string | null | undefined,
): void {
  const raw = bar.by_date;
  if (!raw?.length) {
    pushTotalRect(rects, `${keyPrefix}-total`, plotRight, top, totalWidth, height);
    return;
  }

  const segments = capVolumeByPriceDateSegments(raw);
  const attributed = segments.reduce((sum, s) => sum + s.volume, 0);
  if (attributed <= 0) {
    pushTotalRect(rects, `${keyPrefix}-total`, plotRight, top, totalWidth, height);
    return;
  }

  let x = plotRight - totalWidth;
  for (const slice of segments) {
    const segW = (slice.volume / attributed) * totalWidth;
    if (segW <= 0.25) continue;
    const highlighted = !!highlightDate && slice.date === highlightDate;
    const label = formatVolumeByPriceDateLabel(slice.date);
    const pct = ((slice.volume / bar.volume) * 100).toFixed(1);
    rects.push({
      key: `${keyPrefix}-date-${slice.date}`,
      x,
      y: top,
      width: segW,
      height,
      fill: volumeByPriceColorForDate(slice.date, dateOrder),
      fillOpacity: highlighted
        ? VOLUME_BY_PRICE_DATE_HIGHLIGHT_OPACITY
        : VOLUME_BY_PRICE_DATE_FILL_OPACITY,
      title: `$${bar.price.toFixed(2)} · ${label}: ${slice.volume.toLocaleString()} (${pct}%)`,
    });
    x += segW;
  }
}

/**
 * Horizontal volume-at-price profile anchored to the right edge of the plot
 * (opposite the Y-axis price labels).
 */
export function VolumeByPriceChartOverlay({
  bars,
  chartProps,
  yAxisId = 'price',
  bucketCents = 1,
  /** Suppress thin tails on large multi-day profiles (still shows all meaningful nodes). */
  minVolumeFrac = 0.0008,
  colorMode = 'total',
  dateOrder = [],
  highlightDate = null,
}: Props) {
  const offset = chartProps.offset;
  const yScale = resolveYScale(chartProps.yAxisMap, yAxisId);
  if (!offset || !yScale || bars.length === 0 || offset.width <= 0) return null;

  const plotRight = offset.left + offset.width;
  const plotTop = offset.top;
  const plotBottom = offset.top + offset.height;
  const maxBarW = offset.width * VOLUME_BY_PRICE_MAX_WIDTH_FRAC;
  const bucketPrice = bucketCents / 100;

  const yAt = (p: number) => plotY(yScale, offset.top, p);

  let maxVol = 0;
  for (const bar of bars) {
    if (bar.volume > maxVol) maxVol = bar.volume;
  }
  if (maxVol <= 0) return null;

  const volFloor = maxVol * minVolumeFrac;
  const sqrtMax = Math.sqrt(maxVol);

  const rects: OverlayRect[] = [];

  for (const bar of bars) {
    const { price, volume } = bar;
    if (volume < volFloor) continue;

    const y0 = yAt(price);
    const y1 = yAt(price + bucketPrice);
    if (y0 == null || y1 == null) continue;

    let top = Math.min(y0, y1);
    let height = Math.abs(y1 - y0);
    if (height < 1) {
      height = 1;
      top -= 0.5;
    }

    const centerY = (top + height) / 2;
    if (centerY < plotTop - 2 || centerY > plotBottom + 2) continue;

    const t = Math.sqrt(volume) / sqrtMax;
    let width = t * maxBarW;
    if (volume > 0 && width < 1.5) width = 1.5;
    width = Math.min(width, maxBarW);

    const keyPrefix = `vp-${price}`;
    if (colorMode === 'byDate') {
      pushDateRects(
        rects,
        keyPrefix,
        plotRight,
        top,
        width,
        height,
        bar,
        dateOrder,
        highlightDate,
      );
    } else if (colorMode === 'bySide') {
      pushSideRects(rects, keyPrefix, plotRight, top, width, height, bar);
    } else {
      pushTotalRect(rects, keyPrefix, plotRight, top, width, height);
    }
  }

  if (!rects.length) return null;

  return (
    <g
      className="recharts-layer volume-by-price-overlay"
      pointerEvents={colorMode === 'byDate' ? 'auto' : 'none'}
      style={{ shapeRendering: 'crispEdges' }}
    >
      {rects.map((r) => (
        <g key={r.key}>
          <rect
            x={r.x}
            y={r.y}
            width={r.width}
            height={r.height}
            fill={r.fill}
            fillOpacity={r.fillOpacity}
          >
            {r.title ? <title>{r.title}</title> : null}
          </rect>
        </g>
      ))}
    </g>
  );
}
